/* =========================================================
   Damissella — damissella.js
   Cart AJAX, cart drawer, gallery, mobile menu, interactions
   ========================================================= */

/* ---------------------------------------------------------
   CART STATE
   --------------------------------------------------------- */
const DamCart = {
  _listeners: [],

  async get() {
    const r = await fetch('/cart.js');
    return r.json();
  },

  async add(variantId, qty = 1, properties = {}) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: qty, properties }),
    });
    if (!r.ok) throw await r.json();
    const cart = await DamCart.get();
    DamCart._notify(cart);
    return cart;
  },

  async change(variantId, qty) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    const cart = await r.json();
    DamCart._notify(cart);
    return cart;
  },

  async remove(variantId) {
    return DamCart.change(variantId, 0);
  },

  onChange(fn) { DamCart._listeners.push(fn); },

  _notify(cart) {
    DamCart._listeners.forEach(fn => fn(cart));
  },
};

/* ---------------------------------------------------------
   CART DRAWER
   --------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer  = this.querySelector('.cart-drawer');
    this.scrim   = this.querySelector('.cart-scrim');
    this.linesEl = this.querySelector('.cart-drawer__lines');
    this.emptyEl = this.querySelector('.cart-drawer__empty');
    this.footEl  = this.querySelector('.cart-drawer__foot');
    this.countEl = this.querySelector('.cart-drawer__count');
    this.closeBtn= this.querySelector('.cart-drawer__close');
    this.totEl   = this.querySelector('[data-cart-subtotal]');
    this.chkBtn  = this.querySelector('.cart-drawer__checkout');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.scrim?.addEventListener('click', () => this.close());
    this.querySelector('.cart-drawer__keep')?.addEventListener('click', () => this.close());

    // Checkout: navegación explícita para evitar problemas con routes.checkout_url
    this.chkBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.href = '/checkout';
    });

    DamCart.onChange(cart => this.render(cart));

    // Init from server-rendered state
    DamCart.get().then(cart => this.render(cart));
  }

  open()  {
    this.drawer?.classList.add('is-open');
    this.scrim?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer?.classList.remove('is-open');
    this.scrim?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  render(cart) {
    // Update nav badge
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });

    if (!this.countEl) return;

    const count = cart.item_count;
    this.countEl.textContent = `${count} ${count === 1 ? 'pieza' : 'piezas'}`;

    if (count === 0) {
      this.emptyEl?.removeAttribute('hidden');
      this.linesEl?.setAttribute('hidden', '');
      this.footEl?.setAttribute('hidden', '');
      return;
    }

    this.emptyEl?.setAttribute('hidden', '');
    this.linesEl?.removeAttribute('hidden');
    this.footEl?.removeAttribute('hidden');

    if (this.linesEl) {
      this.linesEl.innerHTML = cart.items.map(item => `
        <div class="cart-line" data-line-key="${item.key}">
          <div class="cart-line__img">
            <a href="${item.url}">
              <img src="${item.image}" alt="${item.product_title}" loading="lazy" width="84" height="100">
            </a>
          </div>
          <div>
            <div class="cart-line__name">${item.product_title}</div>
            <div class="cart-line__variant">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
            <div class="cart-line__controls">
              <div class="cart-qty">
                <button class="cart-qty__btn" aria-label="Reducir cantidad" data-action="decrease" data-id="${item.variant_id}">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                    <path d="M5 12h14"/>
                  </svg>
                </button>
                <span class="cart-qty__num">${item.quantity}</span>
                <button class="cart-qty__btn" aria-label="Aumentar cantidad" data-action="increase" data-id="${item.variant_id}" data-current="${item.quantity}">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </button>
              </div>
              <button class="cart-line__remove" data-action="remove" data-id="${item.variant_id}">Eliminar</button>
            </div>
          </div>
          <div class="cart-line__price">${damFormatMoney(item.line_price)}</div>
        </div>
      `).join('');

      // Bind qty events
      this.linesEl.querySelectorAll('[data-action]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const id  = parseInt(btn.dataset.id);
          const act = btn.dataset.action;
          const line = btn.closest('.cart-line');
          const numEl = line.querySelector('.cart-qty__num');
          const cur = parseInt(numEl?.textContent || 1);

          if (act === 'increase')  await DamCart.change(id, cur + 1);
          if (act === 'decrease')  await DamCart.change(id, Math.max(0, cur - 1));
          if (act === 'remove')    await DamCart.remove(id);
        });
      });
    }

    if (this.totEl) {
      this.totEl.textContent = damFormatMoney(cart.total_price);
    }

    if (this.chkBtn) {
      this.chkBtn.textContent = `Ir al pago · ${damFormatMoney(cart.total_price)}`;
      this.chkBtn.href = '/checkout';
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ---------------------------------------------------------
   ADD TO CART FORM
   --------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form     = this.querySelector('form');
    this.addBtn   = this.querySelector('[data-add-btn]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.variantId= null;

    this.form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleAdd();
    });

    // Qty controls
    this.querySelector('[data-qty-dec]')?.addEventListener('click', () => {
      const v = Math.max(1, parseInt(this.qtyInput?.value || 1) - 1);
      if (this.qtyInput) this.qtyInput.value = v;
      this.updateQtyDisplay(v);
    });

    this.querySelector('[data-qty-inc]')?.addEventListener('click', () => {
      const v = parseInt(this.qtyInput?.value || 1) + 1;
      if (this.qtyInput) this.qtyInput.value = v;
      this.updateQtyDisplay(v);
    });
  }

  updateQtyDisplay(v) {
    const disp = this.querySelector('[data-qty-display]');
    if (disp) disp.textContent = v;
  }

  async handleAdd() {
    const variantEl = this.querySelector('[name="id"]');
    const id = parseInt(variantEl?.value);
    const qty = parseInt(this.qtyInput?.value || 1);

    if (!id) return;

    // Loading state
    const origText = this.addBtn?.innerHTML;
    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.innerHTML = '<span class="loading-spin"></span>';
    }

    try {
      await DamCart.add(id, qty);

      // Success state
      if (this.addBtn) {
        this.addBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M5 12l5 5L20 7"/></svg>
          &nbsp;Agregado a la bolsa
        `;
        this.addBtn.classList.add('is-added');
      }

      // Open cart drawer
      document.querySelector('cart-drawer')?.open();

      // Restore after delay
      setTimeout(() => {
        if (this.addBtn) {
          this.addBtn.innerHTML = origText;
          this.addBtn.disabled = false;
          this.addBtn.classList.remove('is-added');
        }
      }, 2500);

    } catch (err) {
      if (this.addBtn) {
        this.addBtn.innerHTML = origText;
        this.addBtn.disabled = false;
      }
      DamToast.show(err.description || 'No fue posible agregar el producto.');
    }
  }
}

customElements.define('product-form', ProductForm);

/* ---------------------------------------------------------
   VARIANT SELECTOR
   --------------------------------------------------------- */
class VariantSelector extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll('[data-variant-btn]');
    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.buttons.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // Update hidden variant id
        const variantId = btn.dataset.variantId;
        const formVariant = document.querySelector('[name="id"]');
        if (formVariant) formVariant.value = variantId;

        // Update price display
        const price = btn.dataset.price;
        if (price) {
          document.querySelectorAll('[data-product-price]').forEach(el => {
            el.textContent = 'S/ ' + (parseInt(price)/100).toFixed(0);
          });
        }

        // Update add btn label
        const addBtn = document.querySelector('[data-add-btn]');
        if (addBtn && price) {
          const orig = addBtn.dataset.origLabel;
          addBtn.textContent = (orig || 'Agregar al carrito') + ' · S/ ' + (parseInt(price)/100).toFixed(0);
        }
      });
    });
  }
}

customElements.define('variant-selector', VariantSelector);

/* ---------------------------------------------------------
   PRODUCT GALLERY
   --------------------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.hero   = this.querySelector('[data-gallery-hero]');
    this.heroImg= this.querySelector('[data-gallery-main]');
    this.thumbs = this.querySelectorAll('[data-gallery-thumb]');

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
    });
  }

  setActive(idx) {
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    this.thumbs[idx]?.classList.add('is-active');

    const src = this.thumbs[idx]?.querySelector('img')?.dataset.fullsrc
              || this.thumbs[idx]?.querySelector('img')?.src;
    if (src && this.heroImg) {
      this.heroImg.src = src;
    }
  }
}

customElements.define('product-gallery', ProductGallery);

/* ---------------------------------------------------------
   COLLECTION FILTER (client-side)
   --------------------------------------------------------- */
class CollectionFilter extends HTMLElement {
  connectedCallback() {
    this.chips = this.querySelectorAll('[data-filter-chip]');
    this.chips.forEach(chip => {
      chip.addEventListener('click', () => {
        this.chips.forEach(c => c.classList.remove('is-active'));
        chip.classList.add('is-active');

        const tag = chip.dataset.filterChip;
        const cards = document.querySelectorAll('[data-product-tag]');

        cards.forEach(card => {
          const matches = tag === 'all' || card.dataset.productTag?.includes(tag);
          card.closest('[data-product-card]').style.display = matches ? '' : 'none';
        });
      });
    });

    // Sort
    const sortEl = this.querySelector('[data-sort-select]');
    sortEl?.addEventListener('change', () => {
      const grid = document.querySelector('[data-product-grid]');
      if (!grid) return;
      const cards = Array.from(grid.querySelectorAll('[data-product-card]'));
      const val = sortEl.value;

      cards.sort((a, b) => {
        const pa = parseInt(a.dataset.price || 0);
        const pb = parseInt(b.dataset.price || 0);
        if (val === 'price-asc')  return pa - pb;
        if (val === 'price-desc') return pb - pa;
        if (val === 'title')  return (a.dataset.title||'').localeCompare(b.dataset.title||'');
        return parseInt(a.dataset.position||0) - parseInt(b.dataset.position||0);
      });

      cards.forEach(c => grid.appendChild(c));
    });
  }
}

customElements.define('collection-filter', CollectionFilter);

/* ---------------------------------------------------------
   MOBILE NAVIGATION
   --------------------------------------------------------- */
function initMobileNav() {
  const toggle = document.querySelector('[data-nav-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const closeBtn  = document.querySelector('[data-nav-close]');

  toggle?.addEventListener('click', () => {
    mobileNav?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  });

  closeBtn?.addEventListener('click', () => {
    mobileNav?.classList.remove('is-open');
    document.body.style.overflow = '';
  });
}

/* ---------------------------------------------------------
   STICKY NAV SHADOW
   --------------------------------------------------------- */
function initNavScroll() {
  const nav = document.querySelector('.dam-nav');
  if (!nav) return;
  const obs = new IntersectionObserver(
    ([e]) => nav.classList.toggle('scrolled', !e.isIntersecting),
    { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
  );
  const sentinel = document.createElement('div');
  document.body.prepend(sentinel);
  obs.observe(sentinel);
}

/* ---------------------------------------------------------
   CART ICON — open drawer on click
   --------------------------------------------------------- */
function initCartToggle() {
  document.querySelectorAll('[data-cart-trigger]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelector('cart-drawer')?.open();
    });
  });
}

/* ---------------------------------------------------------
   TOAST
   --------------------------------------------------------- */
const DamToast = {
  _el: null,
  _timer: null,

  show(msg, duration = 3000) {
    if (!DamToast._el) {
      DamToast._el = document.createElement('div');
      DamToast._el.className = 'dam-toast';
      document.body.appendChild(DamToast._el);
    }
    DamToast._el.textContent = msg;
    DamToast._el.classList.add('is-visible');
    clearTimeout(DamToast._timer);
    DamToast._timer = setTimeout(() => DamToast._el.classList.remove('is-visible'), duration);
  },
};

/* ---------------------------------------------------------
   UTILITY
   --------------------------------------------------------- */
function damFormatMoney(cents) {
  return 'S/ ' + (cents / 100).toFixed(0);
}

/* ---------------------------------------------------------
   ACCORDION (product detail)
   --------------------------------------------------------- */
function initAccordions() {
  document.querySelectorAll('.product-accordion').forEach(acc => {
    const summary = acc.querySelector('summary');
    const iconOpen  = summary?.querySelector('[data-icon-open]');
    const iconClose = summary?.querySelector('[data-icon-close]');

    acc.addEventListener('toggle', () => {
      if (iconOpen)  iconOpen.style.display  = acc.open ? 'none' : '';
      if (iconClose) iconClose.style.display = acc.open ? '' : 'none';
    });
  });
}

/* ---------------------------------------------------------
   NEWSLETTER FORM (footer)
   --------------------------------------------------------- */
function initNewsletter() {
  document.querySelectorAll('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      if (!input?.value) return;

      // Shopify newsletter handled server-side via Klaviyo/MailChimp app
      // or native Shopify customer creation. This just provides UX feedback.
      form.reset();
      DamToast.show('Gracias — te escribimos cuando haya novedades.');
    });
  });
}

/* ---------------------------------------------------------
   INIT
   --------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  initMobileNav();
  initNavScroll();
  initCartToggle();
  initAccordions();
  initNewsletter();
});
